"use client";

import { useLocale } from "@i18n/react";
import { useQueryClient } from "@tanstack/react-query";

import type { Journal } from "../../../lib";
import { journalSchema } from "../../../lib";
import type { UseRequestQueryProps } from "../../../request";
import { useRequestMutation, useRequestQuery } from "../../../request";
import { keyJournal, keyJournals } from "./keyJournals";

export type UseRequestJournalProps = UseRequestQueryProps & {
  id: number;
};

export const useRequestJournal = ({ id, ...props }: UseRequestJournalProps) => {
  const queryClient = useQueryClient();
  const locale = useLocale();

  return {
    query: useRequestQuery<typeof journalSchema>({
      enabled: typeof id === "number",
      enabledWithoutSessionToken: false,
      queryFnRequestProps: {
        urlBackendPath: `/journals/${id}`,
        schema: journalSchema,
      },
      queryKey: keyJournal({ id }),
      ...props,
    }),
    mutationPut: useRequestMutation<typeof journalSchema, { journal: Journal }>(
      {
        mutationFnRequestProps: ({ journal }) => ({
          method: "PUT",
          urlBackendPath: `/journals/${journal.sync_uuid}`,
          schema: journalSchema,
          body: JSON.stringify(journal),
        }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [keyJournal({ id })],
          });
          queryClient.invalidateQueries({ queryKey: [keyJournals()] });
        },
      },
    ),
    mutationDelete: useRequestMutation<typeof journalSchema, { uuid: string }>({
      mutationFnRequestProps: ({ uuid }) => ({
        method: "DELETE",
        urlBackendPath: `/journals/${uuid}`,
        schema: journalSchema,
      }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [keyJournal({ id })],
        });
        queryClient.invalidateQueries({ queryKey: [keyJournals()] });
      },
    }),
  };
};
